import { Helmet } from "react-helmet-async";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

import { useTranslation } from "react-i18next";
import { ArrowRight, Gauge, CircleSlash, PiggyBank, ShieldCheck } from "lucide-react";
import cetafuelTug from "@/assets/cetafuel-tug-visual.png";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import cetafuelVisual from "@/assets/cetafuel-hero.png";
import ihelmPlatform from "@/assets/ihelm-platform-clean.png";

const CetaFuel = () => {
  
  const { t, i18n } = useTranslation('cetafuel');

  const opportunities = [
    { icon: PiggyBank, title: t('opportunity.capex'), description: t('opportunity.capexDesc') },
    { icon: CircleSlash, title: t('opportunity.noInstall'), description: t('opportunity.noInstallDesc') },
    { icon: Gauge, title: t('opportunity.accuracy'), description: t('opportunity.accuracyDesc') },
    { icon: ShieldCheck, title: t('opportunity.noClog'), description: t('opportunity.noClogDesc') },
  ];

  const faqs = Array.from({ length: 4 }, (_, i) => ({
    question: t(`faq.q${i + 1}`),
    answer: t(`faq.a${i + 1}`),
  }));

  return (
    <div className="min-h-screen section-fade">
      <Helmet>
        <html lang={i18n.language} />
        <title>{t('meta.title')}</title>
        <meta name="description" content={t('meta.description')} />
        <link rel="canonical" href="https://cetasol.com/cetafuel" />
        <meta property="og:title" content={t('meta.title')} />
        <meta property="og:description" content={t('meta.description')} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://cetasol.com/cetafuel" />
        <meta property="og:image" content="https://cetasol.com/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t('meta.title')} />
        <meta name="twitter:description" content={t('meta.description')} />
        <meta name="twitter:image" content="https://cetasol.com/og-image.jpg" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://cetasol.com/" },
            { "@type": "ListItem", "position": 2, "name": "CetaFuel", "item": "https://cetasol.com/cetafuel" }
          ]
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "CetaFuel",
          "description": t('meta.description'),
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "Web",
          "provider": { "@type": "Organization", "name": "Cetasol" },
          "url": "https://cetasol.com/cetafuel"
        })}</script>
      </Helmet>
      <Header />
      <main className="pb-28">
        {/* Hero */}
        <section className="relative min-h-[85vh] flex items-end overflow-hidden mb-28">
          <img src={cetafuelVisual} alt="CetaFuel virtual fuel sensing technology" className="absolute inset-0 w-full h-full object-cover object-right" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-r from-background/90 via-background/60 to-transparent" />
          <div className="relative z-10 container mx-auto px-6 pb-16 pt-32">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-2xl">
              <p className="text-xs font-semibold tracking-widest uppercase text-primary mb-4">{t('hero.label')}</p>
              <h1 className="font-display text-4xl md:text-5xl font-bold text-primary-foreground mb-3 leading-tight">CetaFuel</h1>
              <p className="text-lg text-primary-foreground font-medium mb-4">{t('hero.subtitle')}</p>
              <p className="text-lg text-primary-foreground/80 leading-relaxed max-w-xl">{t('hero.description')}</p>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.25 }} className="grid grid-cols-3 divide-x divide-primary-foreground/20 mt-12 max-w-3xl">
              <div className="pr-8">
                <span className="font-display text-4xl font-bold text-primary-foreground">{t('hero.stat1Value')}</span>
                <p className="text-sm text-primary-foreground/70 mt-2">{t('hero.stat1Label')}</p>
              </div>
              <div className="px-8">
                <span className="font-display text-4xl font-bold text-primary-foreground">{t('hero.stat2Value')}</span>
                <p className="text-sm text-primary-foreground/70 mt-2">{t('hero.stat2Label')}</p>
              </div>
              <div className="pl-8">
                <span className="font-display text-4xl font-bold text-primary-foreground">{t('hero.stat3Value')}</span>
                <p className="text-sm text-primary-foreground/70 mt-2">{t('hero.stat3Label')}</p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Opportunities */}
        <section className="container mx-auto px-6 mb-28">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7 }} className="mb-8 text-center">
            <p className="text-xs font-semibold tracking-widest uppercase text-section-label mb-4">{t('opportunity.label')}</p>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4">{t('opportunity.heading')}</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">{t('opportunity.description')}</p>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {opportunities.map((item, i) => (
              <motion.div key={item.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }} className="flex items-start gap-3">
                <span className="mt-2 h-2 w-2 rounded-full bg-primary shrink-0" />
                <div>
                  <h3 className="font-display text-base font-bold text-foreground mb-1">{item.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Details */}
        <section className="bg-surface-light py-24 mb-28">
          <div className="container mx-auto px-6">
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="text-center mb-10">
              <p className="text-xs font-semibold tracking-widest uppercase text-foreground mb-4">{t('details.label')}</p>
              <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground">{t('details.heading')}</h2>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
              <div>
                <p className="text-muted-foreground leading-relaxed mb-3">{t('details.p1')}</p>
                <p className="text-muted-foreground leading-relaxed mb-3">{t('details.p2')}</p>
                <p className="text-muted-foreground leading-relaxed">{t('details.p3')}</p>
              </div>
              <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.15 }}>
                <img src={cetafuelTug} alt="Tugboat with CetaFuel virtual fuel sensor technology overlay" className="w-full " loading="lazy" />
              </motion.div>
            </div>
          </div>
        </section>

        {/* Combine with iHelm */}
        <section className="container mx-auto px-6 mb-28">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
              <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-4">{t('combineIhelm.heading')}</h2>
              <p className="text-muted-foreground leading-relaxed mb-6">{t('combineIhelm.description')}</p>
              <Link to="/ihelm-intelligent-platform" className="inline-flex items-center gap-2 border border-[hsl(var(--secondary-border))] text-foreground bg-secondary/18 px-6 py-3 rounded-md font-medium text-sm hover:bg-secondary/18 transition-colors">
                {t('combineIhelm.cta')} <ArrowRight size={16} />
              </Link>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.15 }}>
              <img src={ihelmPlatform} alt="iHelm vessel performance management platform" className="w-full " loading="lazy" />
            </motion.div>
          </div>
        </section>

        {/* Segment links */}
        <section className="bg-surface-light py-24 mb-28">
          <div className="container mx-auto px-6">
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
              <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-4 text-center">{t('segmentLinks.heading')}</h2>
              <p className="text-muted-foreground mb-8 max-w-lg mx-auto text-center">{t('segmentLinks.description')}</p>
              <div className="flex flex-wrap justify-center gap-3">
                {[
                  { label: "Ferry", to: "/segments/ferry" },
                  { label: "Tug", to: "/segments/tug" },
                  { label: "Merchant", to: "/segments/merchant" },
                  { label: "CTV", to: "/segments/ctv" },
                  { label: "Pilot", to: "/segments/pilot" },
                  { label: "Fishing", to: "/segments/fishing" },
                ].map((s) => (
                  <Link key={s.label} to={s.to} className="px-5 py-2.5 rounded-md border border-border text-sm font-medium text-foreground hover:bg-secondary/18 transition-colors">{s.label}</Link>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* White paper CTA */}
        <section className="container mx-auto px-6 mb-28">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="max-w-5xl mx-auto border border-border p-8 md:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6"
          >
            <div>
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Download white paper</p>
              <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground">
                Learn more about fuel reading with 90% CAPEX savings
              </h2>
            </div>
            <Link
              to="/whitepaper/cetafuel-download"
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground hover:bg-[#14699B] px-6 py-3 rounded-md font-medium text-sm transition-colors shrink-0"
            >
              Download now <ArrowRight size={16} />
            </Link>
          </motion.div>
        </section>

        {/* FAQ */}
        <section className="container mx-auto px-6 mb-28">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="max-w-5xl mx-auto border border-border p-8 md:p-10"
          >
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-8">{t('faq.heading')}</h2>
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, i) => (
                <AccordionItem key={i} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left font-display font-normal text-sm md:text-base text-foreground">{faq.question}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed">{faq.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default CetaFuel;
